/**
 * Renders the floor plan / housing unit layout image shared by floorPlanDetail's
 * [data-secondary-panel] (desktop) and mobile image-expand screen, and housingUnitDetail's
 * inline [data-panel] content. Pure function — no DOM mounting, no event logic — matching this
 * folder's existing .dom.js convention (see floorPlanCards.dom.js, quickMoveInCards.dom.js).
 *
 * Uses the same `data-siteplan-card-image` attribute the card components use, so it's covered
 * for free by the existing broken-image fallback (handlers/imageErrorHandler.js).
 *
 * @module nativeInteractiveSiteMap/components/floorPlanImage.dom
 */

/**
 * Derives the accessible alt text for a floor plan's layout image from its plan object. Floor
 * plan-specific (housingUnitDetail.plugin.js's inline image has its own, different fallback
 * text, since a housing unit isn't a plan) — shared only between floorPlanDetail.plugin.js's
 * secondary panel and its mobile floorPlanViewer.dom.js screen, which both show the same plan's
 * image and previously each carried their own identical copy of this function.
 * @param {object|null} plan
 * @returns {string}
 */
export function deriveFloorPlanImageAlt(plan) {
  return plan?.name ? `${plan.name} floor plan configuration image` : 'floor plan configuration image';
}

/**
 * @param {object} params
 * @param {string|null} params.imageUrl
 * @param {string} [params.imageAlt]
 * @returns {string} HTML string.
 */
export default function renderFloorPlanImage({ imageUrl, imageAlt = 'floor plan layout image' }) {
  return `
    <div class="nhs-u-height--full nhs-u-width--full" data-qa="native_ism_floor_plan_image_wrapper">
      <img class="nhs-n1-u-object-fit--contain nhs-u-display--block nhs-u-height--full nhs-u-width--full"
           data-siteplan-card-image
           loading="lazy"
           src="${imageUrl}"
           alt="${imageAlt}"
           data-qa="native_ism_floor_plan_image" />
    </div>
  `;
}
